import { useState, useEffect } from 'preact/hooks';
import { Card } from '../../components/card';
import { Input } from '../../components/input';
import { Button } from '../../components/button';
import { Message } from '../../components/message';
import { getVersionSettings, updateVersionSettings } from '../../api/version';
import type { VersionSettings } from '../../api/types';

export function VersionTab() {
  const [settings, setSettings] = useState<VersionSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Form state
  const [iosVersion, setIosVersion] = useState('');
  const [androidVersion, setAndroidVersion] = useState('');

  // Load current settings
  useEffect(() => {
    loadSettings();
  }, []);

  async function loadSettings() {
    try {
      setLoading(true);
      setError(null);
      const data = await getVersionSettings();
      setSettings(data);
      setIosVersion(data.iosMinimumVersion);
      setAndroidVersion(data.androidMinimumVersion);
      setHasChanges(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }

  function handleIosChange(value: string) {
    setIosVersion(value);
    setHasChanges(true);
    setSuccess(null);
  }

  function handleAndroidChange(value: string) {
    setAndroidVersion(value);
    setHasChanges(true);
    setSuccess(null);
  }

  async function handleSave() {
    // Validate version format
    const versionRegex = /^\d+\.\d+\.\d+$/;
    if (!versionRegex.test(iosVersion)) {
      setError('Invalid iOS version format. Use x.x.x (e.g., 1.2.0)');
      return;
    }
    if (!versionRegex.test(androidVersion)) {
      setError('Invalid Android version format. Use x.x.x (e.g., 1.2.0)');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await updateVersionSettings({
        iosMinimumVersion: iosVersion,
        androidMinimumVersion: androidVersion,
      });
      setSuccess('Settings saved successfully');
      setHasChanges(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  }

  function handleReset() {
    if (settings) {
      setIosVersion(settings.iosMinimumVersion);
      setAndroidVersion(settings.androidMinimumVersion);
      setHasChanges(false);
      setError(null);
      setSuccess(null);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px' }}>
      {/* Header */}
      <div style={{ marginBottom: '8px' }}>
        <h1 style={{ margin: '0 0 8px 0', fontSize: '1.5rem' }}>Version Settings</h1>
        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
          Control minimum app versions for mobile clients. Users below these versions will see an update screen.
        </p>
      </div>

      {/* Messages */}
      {error && (
        <Message text={error} type="error" onDismiss={() => setError(null)} />
      )}
      {success && (
        <Message text={success} type="success" onDismiss={() => setSuccess(null)} />
      )}

      {/* Settings Form */}
      <Card>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* iOS Minimum Version */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              iOS Minimum Version
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <Input
                  value={iosVersion}
                  onChange={handleIosChange}
                  placeholder="e.g., 1.2.0"
                />
              </div>
            </div>
            <p
              style={{
                margin: '6px 0 0 0',
                fontSize: '0.75rem',
                color: 'var(--text-secondary)',
              }}
            >
              Minimum iOS app version required. Use format: x.x.x (e.g., 1.2.0)
            </p>
          </div>

          {/* Android Minimum Version */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              Android Minimum Version
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <Input
                  value={androidVersion}
                  onChange={handleAndroidChange}
                  placeholder="e.g., 1.2.0"
                />
              </div>
            </div>
            <p
              style={{
                margin: '6px 0 0 0',
                fontSize: '0.75rem',
                color: 'var(--text-secondary)',
              }}
            >
              Minimum Android app version required. Use format: x.x.x (e.g., 1.2.0)
            </p>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              variant={hasChanges ? 'primary' : 'ghost'}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              onClick={handleReset}
              disabled={saving || !hasChanges}
              variant="ghost"
            >
              Reset
            </Button>
          </div>
        </div>
      </Card>

      {/* Current Status */}
      <Card>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '1.125rem' }}>Current Status</h3>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
          }}
        >
          <div
            style={{
              padding: '16px',
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '8px',
            }}
          >
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>
              iOS Minimum
            </div>
            <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{settings?.iosMinimumVersion || '1.0.0'}</div>
          </div>
          <div
            style={{
              padding: '16px',
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '8px',
            }}
          >
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>
              Android Minimum
            </div>
            <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{settings?.androidMinimumVersion || '1.0.0'}</div>
          </div>
        </div>
      </Card>

      {/* API Info */}
      <Card>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '1.125rem' }}>API Endpoint</h3>
        <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
          Mobile apps check <code>GET /api/version</code> on startup. The response includes minimum
          versions. Users with versions below these will see an update screen.
        </p>
      </Card>
    </div>
  );
}
